/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { Building2, FileText, Layers3, LogIn } from 'lucide-react';
import { NavLink } from 'react-router';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';
import { APPLICATION_VERSION, CONTACT_DETAILS } from '@/config/footerContent';

/* Import libraries */
import {
    APP_NAV_BUTTON_ACTIVE_CLASS,
    APP_NAV_BUTTON_BASE_CLASS,
    APP_NAV_BUTTON_INACTIVE_CLASS,
} from '@/lib/presentation/navigationStyles';

const FOOTER_LINKS = [
    {
        icon: LogIn,
        label: 'Login',
        requiresLoggedOut: true,
        to: APP_ROUTES.login,
    },
    {
        icon: Building2,
        label: 'Contact',
        requiresLoggedOut: false,
        to: APP_ROUTES.contact,
    },
    {
        icon: FileText,
        label: 'Imprint',
        requiresLoggedOut: false,
        to: APP_ROUTES.imprint,
    },
    {
        icon: Layers3,
        label: 'Versions & Licenses',
        requiresLoggedOut: false,
        to: APP_ROUTES.versions,
    },
] as const;

interface AppFooterProps {
    hasSession?: boolean;
}

function AppFooter({ hasSession = false }: AppFooterProps) {
    const { t } = useI18n();
    const visibleFooterLinks = FOOTER_LINKS.filter((footerLink) =>
        footerLink.requiresLoggedOut ? !hasSession : true,
    );

    return (
        <footer className="app-footer border-t border-base-300 bg-base-100/90 backdrop-blur">
            <div className="flex w-full flex-col gap-4 px-4 py-4 lg:px-6">
                <nav
                    className="flex flex-wrap items-center justify-center gap-2"
                    aria-label={t('Footer navigation')}
                >
                    {visibleFooterLinks.map((footerLink) => {
                        const Icon = footerLink.icon;

                        return (
                            <NavLink
                                key={footerLink.to}
                                to={footerLink.to}
                                className={({ isActive }) =>
                                    `${APP_NAV_BUTTON_BASE_CLASS} ${
                                        isActive
                                            ? APP_NAV_BUTTON_ACTIVE_CLASS
                                            : APP_NAV_BUTTON_INACTIVE_CLASS
                                    }`
                                }
                            >
                                <Icon className="h-4 w-4" aria-hidden="true" />
                                {t(footerLink.label)}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs text-base-content/60">
                    <a
                        href="https://www.ea-energie.de/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-inherit no-underline hover:underline"
                    >
                        {CONTACT_DETAILS.copyright} {CONTACT_DETAILS.company}
                    </a>
                    <span aria-hidden="true">•</span>
                    <NavLink
                        to={APP_ROUTES.versions}
                        className="text-inherit no-underline hover:underline"
                    >
                        {t('Version')} {APPLICATION_VERSION}
                    </NavLink>
                </div>
            </div>
        </footer>
    );
}

export default AppFooter;
